import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import PortfolioRequired from '../components/PortfolioRequired';
import EmptyState from '../components/EmptyState';
import AllocationLocationSelector from '../components/allocation/AllocationLocationSelector';
import StockTransactionModal from '../components/StockTransactionModal';
import { PlannerHeader, PlannerModal, generateDatedPlanName } from '../components/allocation/PlannerPresentation';
import StockAllocationTable, { StockAllocationValue } from '../components/allocation/StockAllocationTable';
import PrecomputedCategoryBreakdown from '../components/allocation/PrecomputedCategoryBreakdown';
import useStockLocations from '../hooks/useStockLocations';
import { SEARCH_STOCKS, GET_STOCK_JUMP_POSITIONS } from '../graphql/queries/stocks';
import { GET_STOCK_ALLOCATION_PLAN, GET_STOCK_ALLOCATION_PLANS } from '../graphql/queries/stockAllocations';
import {
  CREATE_STOCK_ALLOCATION_PLAN, UPDATE_STOCK_ALLOCATION_PLAN,
  DELETE_STOCK_ALLOCATION_PLAN, RECORD_STOCK_ALLOCATION_BUY,
} from '../graphql/mutations/stockAllocations';

const inputClass = 'w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';
const buttonClass = 'rounded border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700';
const primaryClass = `${buttonClass} bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-700`;
const decimal = (value) => String(value ?? '').trim().replace(',', '.');
const validAmount = (value) => /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(decimal(value));
const defaultStockBudget = (portfolioId) => {
  try {
    const saved = window.localStorage.getItem(`stockAllocationLastAmount_${portfolioId}`);
    return saved && validAmount(saved) && !/^0+(?:\.0*)?$/.test(decimal(saved)) ? decimal(saved) : '100';
  } catch (_) { return '100'; }
};
const identity = (row) => row.id || row.localId;
const savedRows = (plan) => (plan?.items || []).map((row) => ({ ...row, targetLocationId: row.targetLocation?.id || '' }));
const rowInput = (row) => ({
  ...(row.id ? { id: row.id } : {}),
  ...(row.stock.id ? { stockId: row.stock.id } : { symbol: row.stock.symbol }),
  amount: decimal(row.amount),
  completed: Boolean(row.completed),
  targetLocationId: row.targetLocationId || null,
});
const signature = (name, rows) => JSON.stringify({ name, items: rows.map(rowInput) });

const errorText = (error) => error?.message || 'Request failed. Please try again.';

// Stock-specific state and authenticated API operations stay separate from crypto.
export const StockAllocationPlanner = ({ portfolio }) => {
  const client = useApolloClient();
  const portfolioId = portfolio.id;
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [name, setName] = useState('');
  const selectedPlanKey = `selectedStockAllocationPlanId_${portfolioId}`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newName, setNewName] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [positionResults, setPositionResults] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selection, setSelection] = useState(null);
  const [amount, setAmount] = useState('0');
  const [targetLocationId, setTargetLocationId] = useState('');
  const [showPlans, setShowPlans] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [renameEntry, setRenameEntry] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [showListing, setShowListing] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [inlineEditing, setInlineEditing] = useState(false);
  const [buyRow, setBuyRow] = useState(null);
  const [completeRow, setCompleteRow] = useState(null);
  const alive = useRef(true);
  const generation = useRef(0);
  const searchGeneration = useRef(0);
  const listingSessionVersion = useRef(0);
  const listingSession = listingSessionVersion.current;
  const serial = useRef(0);
  const working = useRef(false);
  const { locations, createLocation, renameLocation, deleteLocation } = useStockLocations(portfolioId);
  const dirty = Boolean(plan && signature(name, rows) !== signature(plan.name, savedRows(plan)));
  const pendingEntry = Boolean(showListing || inlineEditing || selection || search.trim() || amount !== '0' || targetLocationId);
  const unsaved = dirty || pendingEntry;
  const guardRef = useRef({ unsaved, busy });
  guardRef.current = { unsaved: unsaved || Boolean(newName.trim()) || Boolean(renameEntry && renameName !== renameEntry.name), busy };

  const resetEntry = useCallback(() => {
    listingSessionVersion.current += 1;
    searchGeneration.current += 1;
    setSearch(''); setResults([]); setPositionResults([]); setPositionsError(''); setSearchError(''); setSearching(false);
    setSelection(null); setAmount('0'); setTargetLocationId(''); setShowListing(false); setEditRow(null); setInlineEditing(false);
  }, []);
  const accept = useCallback((next) => {
    setPlan(next); setName(next?.name || ''); setRows(savedRows(next));
    resetEntry();
  }, [resetEntry]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; generation.current += 1; searchGeneration.current += 1; };
  }, []);

  useEffect(() => {
    const beforeSwitch = (event) => {
      if (working.current || guardRef.current.busy || (guardRef.current.unsaved && !window.confirm('Discard unsaved stock allocation changes and switch portfolios?'))) event.preventDefault();
    };
    const beforeUnload = (event) => {
      if (guardRef.current.unsaved || working.current) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('portfolio:before-select', beforeSwitch);
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('portfolio:before-select', beforeSwitch);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);

  const fetchPlans = useCallback(async () => {
    const response = await client.query({ query: GET_STOCK_ALLOCATION_PLANS, variables: { portfolioId }, fetchPolicy: 'network-only' });
    return response.data.stockAllocationPlans;
  }, [client, portfolioId]);
  const fetchPlan = useCallback(async (planId) => {
    const response = await client.query({ query: GET_STOCK_ALLOCATION_PLAN, variables: { portfolioId, planId }, fetchPolicy: 'network-only' });
    if (!response.data.stockAllocationPlan) throw new Error('This plan is no longer available. Refresh the plan list.');
    return response.data.stockAllocationPlan;
  }, [client, portfolioId]);

  useEffect(() => {
    const token = ++generation.current;
    setLoading(true); setError('');
    fetchPlans().then(async (list) => {
      if (!alive.current || token !== generation.current) return;
      setPlans(list);
      // Like crypto, reopen the selected plan (or the latest plan) without a
      // second selection dialog. Validate the saved ID against this portfolio.
      const savedId = window.localStorage.getItem(selectedPlanKey);
      const chosen = list.find((entry) => entry.id === savedId) || [...list].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))[0];
      if (chosen) {
        const next = await fetchPlan(chosen.id);
        if (alive.current && token === generation.current) {
          accept(next);
          window.localStorage.setItem(selectedPlanKey, next.id);
        }
      }
    }).catch((failure) => {
      if (alive.current && token === generation.current) setError(errorText(failure));
    }).finally(() => {
      if (alive.current && token === generation.current) setLoading(false);
    });
  }, [accept, fetchPlan, fetchPlans, selectedPlanKey]);

  useEffect(() => {
    if (!showListing || editRow) return undefined;
    const session = listingSessionVersion.current;
    let cancelled = false;
    setPositionResults([]); setPositionsError(''); setPositionsLoading(true);
    client.query({ query: GET_STOCK_JUMP_POSITIONS, variables: { portfolioId }, fetchPolicy: 'network-only' })
      .then(({ data }) => {
        if (!cancelled && alive.current && session === listingSessionVersion.current) setPositionResults(data?.stockJumpPositions || []);
      })
      .catch((failure) => {
        if (!cancelled && alive.current && session === listingSessionVersion.current) setPositionsError(errorText(failure));
      })
      .finally(() => {
        if (!cancelled && alive.current && session === listingSessionVersion.current) setPositionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [showListing, editRow, client, portfolioId]);

  const existingStockIds = new Set(rows.map((row) => row.stock.id).filter(Boolean));
  const positionSuggestions = positionResults.filter((stock) => !existingStockIds.has(stock.stockId) && (`${stock.name} ${stock.symbol}`).toLowerCase().includes(search.trim().toLowerCase()));
  const suggestions = [...positionSuggestions, ...results.filter((stock) => !existingStockIds.has(stock.stockId) && !positionSuggestions.some((position) => position.stockId === stock.stockId))];

  useEffect(() => {
    const token = ++searchGeneration.current;
    setResults([]); setSearchError('');
    const query = search.trim();
    if (!plan || query.length < 2 || selection) { setSearching(false); return undefined; }
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const response = await client.query({ query: SEARCH_STOCKS, variables: { query }, fetchPolicy: 'network-only' });
        if (alive.current && token === searchGeneration.current) setResults(response.data?.searchStocks || []);
      } catch (failure) {
        if (alive.current && token === searchGeneration.current) setSearchError(errorText(failure));
      } finally {
        if (alive.current && token === searchGeneration.current) setSearching(false);
      }
    }, 300);
    return () => { window.clearTimeout(timer); searchGeneration.current += 1; };
  }, [client, search, selection, plan]);

  const allowDiscard = () => !unsaved || window.confirm('Discard unsaved changes to this plan?');
  const start = () => {
    if (working.current) return null;
    working.current = true; setBusy(true); setError(''); setNotice('');
    return ++generation.current;
  };
  const current = (token) => alive.current && token === generation.current;
  const currentListing = (token, session) => current(token) && session === listingSessionVersion.current;
  const finish = (token) => {
    if (current(token)) { working.current = false; setBusy(false); }
  };

  const selectPlan = async (id) => {
    if (working.current || !allowDiscard()) return;
    const token = start();
    resetEntry();
    try {
      const next = id ? await fetchPlan(id) : null;
      if (current(token)) {
        accept(next);
        if (next) window.localStorage.setItem(selectedPlanKey, next.id);
        else window.localStorage.removeItem(selectedPlanKey);
      }
    }
    catch (failure) { if (current(token)) setError(errorText(failure)); }
    finally { finish(token); }
  };

  const createPlan = async (event) => {
    event.preventDefault();
    if (!newName.trim() || working.current || !allowDiscard()) return;
    const token = start();
    try {
      const response = await client.mutate({ mutation: CREATE_STOCK_ALLOCATION_PLAN, variables: {
        portfolioId, name: newName.trim(), currency: portfolio.currency || 'USD', items: [],
      } });
      if (!current(token)) return;
      const next = response.data.createStockAllocationPlan;
      accept(next); setPlans((list) => [...list, next]); setNewName(''); setShowCreate(false); setShowPlans(false);
      window.localStorage.setItem(selectedPlanKey, next.id);
      setNotice('Plan created. Its budget currency is fixed.');
    } catch (failure) { if (current(token)) setError(errorText(failure)); }
    finally { finish(token); }
  };

  const persist = async (nextRows, nextName = name) => {
    if (!plan || working.current) return;
    if (!nextName.trim()) { setError('Enter a plan name.'); return; }
    if (nextRows.some((row) => !validAmount(row.amount))) { setError('Budgets must be non-negative decimals, for example 0 or 125,50.'); return; }
    const token = start();
    let confirmedSave = false;
    try {
      const response = await client.mutate({ mutation: UPDATE_STOCK_ALLOCATION_PLAN, variables: {
        portfolioId, planId: plan.id, name: nextName.trim(), items: nextRows.map(rowInput),
      } });
      if (!current(token)) return;
      confirmedSave = true;
      const saved = response.data.updateStockAllocationPlan;
      accept(saved); setPlans((list) => list.map((entry) => entry.id === saved.id ? saved : entry));
      // Re-fetch estimates and category weights; never re-send a confirmed save.
      const refreshed = await fetchPlan(saved.id);
      if (current(token)) accept(refreshed);
    } catch (failure) {
      if (current(token)) setError(`${confirmedSave ? 'Plan saved, but refresh failed. Use Refresh; do not save again to retry the refresh. ' : ''}${errorText(failure)}`);
    } finally { finish(token); }
  };
  const save = () => {
    if (pendingEntry) return;
    return persist(rows);
  };

  const removePlan = async (entry) => {
    if (!entry || working.current || !window.confirm(`Delete “${entry.name}” and its allocation rows? Recorded trades are kept.${unsaved && entry.id === plan?.id ? ' Unsaved edits will be discarded.' : ''}`)) return;
    const token = start();
    try {
      const response = await client.mutate({ mutation: DELETE_STOCK_ALLOCATION_PLAN, variables: { portfolioId, planId: entry.id } });
      if (response.data.deleteStockAllocationPlan !== true) throw new Error('Plan was not deleted.');
      if (current(token)) {
        setPlans((list) => list.filter((item) => item.id !== entry.id));
        if (entry.id === plan?.id) { accept(null); window.localStorage.removeItem(selectedPlanKey); }
        setNotice('Plan deleted. Recorded trades were not deleted.');
      }
      const list = await fetchPlans();
      if (current(token)) setPlans(list);
    } catch (failure) { if (current(token)) setError(errorText(failure)); }
    finally { finish(token); }
  };

  const changeRow = (id, changes) => {
    if (working.current) return;
    const next = rows.map((row) => identity(row) === id ? { ...row, ...changes } : row);
    setRows(next);
    // An inline amount or Done toggle behaves like crypto's saved edit.
    // Never auto-save an open listing dialog; its Apply button owns that edit.
    persist(next);
  };
  const changeCompletion = (id, completed) => {
    if (working.current) return;
    if (dirty) { setError('Retry saving unsaved changes before completing this allocation.'); return; }
    const row = rows.find((entry) => identity(entry) === id);
    if (!row) return;
    if (completed && row.id && !row.completedTransactionId && !/^0+(?:\.0*)?$/.test(decimal(row.amount))) {
      // Crypto suggests a buy after Done. Stock must decide before completion:
      // a recorded buy and its position update are one atomic backend operation.
      setCompleteRow(row);
    } else {
      changeRow(id, { completed });
    }
  };
  const renamePlan = async (event) => {
    event.preventDefault();
    if (!renameEntry || !renameName.trim() || working.current || (renameEntry.id === plan?.id && !allowDiscard())) return;
    const token = start();
    try {
      // Omit items: renaming must not replace rows or their stable IDs.
      const response = await client.mutate({ mutation: UPDATE_STOCK_ALLOCATION_PLAN, variables: { portfolioId, planId: renameEntry.id, name: renameName.trim() } });
      if (current(token)) {
        const saved = response.data.updateStockAllocationPlan;
        setPlans((list) => list.map((entry) => entry.id === saved.id ? saved : entry));
        if (saved.id === plan?.id) accept(saved);
        setRenameEntry(null); setRenameName(''); setNotice('Plan renamed.');
      }
    } catch (failure) { if (current(token)) setError(errorText(failure)); }
    finally { finish(token); }
  };
  const openListing = (row = null) => {
    if (working.current || inlineEditing) return;
    resetEntry(); setEditRow(row); setShowListing(true);
    if (row) { setSelection(row.stock); setSearch(row.stock.name); setAmount(String(row.amount)); setTargetLocationId(row.targetLocationId || ''); }
    else setAmount(defaultStockBudget(portfolioId));
  };
  const addRow = () => {
    if (!selection || !validAmount(amount)) { setError('Select an exact listing and enter a non-negative decimal budget.'); return; }
    if (editRow) {
      changeRow(identity(editRow), { amount: decimal(amount), targetLocationId, targetLocation: null });
      setError(''); resetEntry(); return;
    }
    const next = [...rows, {
      localId: `draft-${++serial.current}`,
      stock: { ...selection, id: selection.stockId || null },
      amount: decimal(amount), completed: false, completedTransactionId: null,
      targetLocationId, nativePrice: null, priceInPlanCurrency: null, estimatedQuantity: null,
    }];
    setRows(next);
    if (!/^0+(?:\.0*)?$/.test(decimal(amount))) {
      try { window.localStorage.setItem(`stockAllocationLastAmount_${portfolioId}`, decimal(amount)); } catch (_) { /* Storage is optional. */ }
    }
    setError(''); resetEntry();
    persist(next);
  };

  const removeRow = (id) => {
    if (working.current) return;
    const next = rows.filter((row) => identity(row) !== id);
    setRows(next);
    persist(next);
  };
  const reorderRows = (from, to) => {
    if (working.current || from === to) return;
    const next = [...rows];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setRows(next);
    persist(next);
  };

  const deleteTarget = async (location) => {
    const token = generation.current;
    const session = listingSessionVersion.current;
    const deleted = await deleteLocation(location);
    if (deleted && alive.current) {
      // Location deletion is immediate, not a draft edit; the backend FK also clears saved targets.
      setRows((existing) => existing.map((row) => row.targetLocationId === location.id ? { ...row, targetLocationId: '', targetLocation: null } : row));
      setPlan((existing) => existing ? { ...existing, items: existing.items.map((row) => row.targetLocation?.id === location.id ? { ...row, targetLocation: null } : row) } : existing);
      setPlans((existing) => existing.map((entry) => ({ ...entry, items: entry.items.map((row) => row.targetLocation?.id === location.id ? { ...row, targetLocation: null } : row) })));
      if (currentListing(token, session)) setTargetLocationId((selected) => selected === location.id ? '' : selected);
    }
    // A stale selector must not clear the target of a reopened listing editor.
    return currentListing(token, session) ? deleted : false;
  };
  const locationProps = {
    locations,
    onCreate: async (locationName) => {
      const token = generation.current;
      const session = listingSessionVersion.current;
      const created = await createLocation(locationName);
      return currentListing(token, session) ? created : null;
    },
    onRename: async (id, locationName) => {
      const token = generation.current;
      const session = listingSessionVersion.current;
      const renamed = await renameLocation(id, locationName);
      return currentListing(token, session) ? renamed : false;
    },
    onDelete: deleteTarget,
    emptySelectionLabel: 'Unassigned',
  };

  const recordBuy = async (variables) => {
    if (!buyRow?.id || dirty || pendingEntry || working.current) throw new Error('Save the plan before recording a buy.');
    const token = start();
    try {
      // Deliberately whitelist the dedicated input. The saved row supplies stock and custody;
      // stock modal field names for historical FX differ from this API's input casing.
      const input = {
        price: variables.price, quantity: variables.quantity, timestamp: variables.timestamp,
        currency: variables.currency, fees: variables.fees,
      };
      const fields = { brokerBaseCurrency: 'brokerBaseCurrency', brokerFXRate: 'brokerFxRate', brokerBaseAmount: 'brokerBaseAmount', fxRateToUSD: 'fxRateToUsd' };
      Object.entries(fields).forEach(([source, target]) => { if (variables[source] !== undefined) input[target] = variables[source]; });
      const response = await client.mutate({ mutation: RECORD_STOCK_ALLOCATION_BUY, variables: { portfolioId, planId: plan.id, itemId: buyRow.id, input } });
      const result = response.data?.recordStockAllocationBuy;
      if (!result?.transactionId) throw new Error('The server did not confirm the recorded trade. Refresh the plan before retrying.');
      if (current(token)) {
        // Lock the row immediately even if subsequent read-back fails.
        const complete = (row) => row.id === result.itemId ? { ...row, completed: true, completedTransactionId: result.transactionId } : row;
        setRows((existing) => existing.map(complete));
        setPlan((existing) => ({ ...existing, items: existing.items.map(complete) }));
      }
      return { data: { addStockTransaction: { success: true, transactionId: result.transactionId } } };
    } finally { finish(token); }
  };

  const refresh = async () => {
    if (working.current || !allowDiscard()) return;
    const token = start();
    try {
      const [list, next] = await Promise.all([fetchPlans(), plan ? fetchPlan(plan.id) : Promise.resolve(null)]);
      if (current(token)) { setPlans(list); accept(next); }
    } catch (failure) { if (current(token)) setError(errorText(failure)); }
    finally { finish(token); }
  };

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 text-gray-900 dark:text-gray-100">
      <PlannerHeader title="Stock Allocation Planner" subtitle={plan ? <span className="inline-flex max-w-full items-center gap-2"><span className="truncate">{plan.name}</span><span title="Fixed budget currency. Changing portfolio display currency does not convert this plan." className="shrink-0 rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-xs">{plan.currency}</span></span> : null} onManagePlans={() => { if (!busy && !loading && !buyRow && !showListing) { if (!plans.length) setNewName(generateDatedPlanName()); setShowPlans(true); } }} />
      {error && <div role="alert" className="mb-3 rounded border border-red-400 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{error}</div>}
      {notice && <p role="status" className="mb-2 text-xs text-green-700 dark:text-green-300">{notice}</p>}
      {loading && <p role="status" className="py-4 text-sm text-gray-500">Loading stock plans…</p>}
      {plan && <>
        {(dirty || pendingEntry || busy || error) && <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span role="status" className={dirty || pendingEntry ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}>{busy ? 'Saving plan…' : dirty || pendingEntry ? 'Unsaved changes · totals reflect the last save' : error ? 'Refresh needed' : 'Plan saved'}</span>
          <div className="flex gap-3">{dirty && !busy && !pendingEntry && <button type="button" onClick={save} className="rounded bg-blue-600 px-3 py-1.5 font-medium text-white hover:bg-blue-700">Retry save</button>}{error && !dirty && <button type="button" onClick={refresh} disabled={busy} className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">Refresh</button>}</div>
        </div>}
        {rows.length > 0 ? <StockAllocationTable key={plan.id} rows={rows} plan={plan} dirty={dirty} pendingEntry={pendingEntry} busy={busy || Boolean(buyRow)} locations={locations} onChange={changeRow} onComplete={changeCompletion} onEdit={openListing} onRemove={removeRow} onReorder={reorderRows} onBuy={setBuyRow} onAdd={() => openListing()} onEditingChange={setInlineEditing} /> : <EmptyState title="No allocations yet" description="Add stocks or ETFs you consider buying to plan your allocation." secondaryAction={() => openListing()} secondaryLabel="+ Add stock / ETF" className="mb-8" />}
        {rows.length > 0 && plan.items.some((item) => !/^0+(?:\.0*)?$/.test(item.amount)) && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" aria-label="Saved allocation breakdowns">
          <PrecomputedCategoryBreakdown title="Sector" categories={plan.breakdowns?.sectors} currency={plan.currency} />
          <PrecomputedCategoryBreakdown title="Instrument type" categories={plan.breakdowns?.instrumentTypes} currency={plan.currency} />
          <PrecomputedCategoryBreakdown title="Location" categories={plan.breakdowns?.locations} currency={plan.currency} />
        </div>}
      </>}
      {!plan && !loading && <div className="rounded-lg bg-white dark:bg-gray-800 shadow-md px-4 py-8 text-center"><h2 className="text-lg font-semibold">{plans.length ? 'Select an allocation plan' : 'No allocation plans found'}</h2><p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{plans.length ? 'Choose a saved stock plan to continue.' : 'Create a new plan to get started!'}</p><button type="button" className={`${primaryClass} mt-4`} onClick={() => { if (!plans.length) setNewName(generateDatedPlanName()); setShowPlans(true); }}>{plans.length ? 'Select Plan' : 'Create Plan'}</button><button type="button" disabled={busy} className="ml-3 text-sm text-blue-600 dark:text-blue-400" onClick={refresh}>Refresh</button></div>}
      {showPlans && <PlannerModal title="Manage Allocation Plans" busy={busy} onClose={() => { if (((newName.trim() && newName !== generateDatedPlanName()) || (renameEntry && renameName !== renameEntry.name)) && !window.confirm('Discard unsaved plan name?')) return; setShowPlans(false); setNewName(''); setShowCreate(false); setRenameEntry(null); setRenameName(''); }}>
        {error && <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <fieldset disabled={busy} className="space-y-6">
          {showCreate || !plans.length ? <form onSubmit={createPlan} className="flex flex-wrap items-center gap-2"><input aria-label="New plan name" autoFocus className={`${inputClass} min-w-0 flex-1`} placeholder="Plan name" value={newName} onChange={(event) => setNewName(event.target.value)} /><button type="button" onClick={() => { setNewName(''); setShowCreate(false); }} className="px-3 py-2 text-sm">Cancel</button><button type="submit" disabled={!newName.trim()} className={primaryClass}>Create</button><p className="w-full text-xs text-gray-500">Budget currency: {portfolio.currency || 'USD'} (fixed at creation)</p></form> : <button type="button" className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600" onClick={() => { setNewName(generateDatedPlanName()); setShowCreate(true); }}>New allocation plan</button>}
          <div><div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your allocation plans</div><div className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md">
            {[...plans].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map((entry) => <div key={entry.id} className={`flex items-stretch ${entry.id === plan?.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'}`}>
              {renameEntry?.id === entry.id ? <form onSubmit={renamePlan} className="flex flex-1 flex-wrap items-center gap-2 px-4 py-3"><input aria-label="Rename plan" autoFocus className={`${inputClass} min-w-0 flex-1`} value={renameName} onChange={(event) => setRenameName(event.target.value)} /><button type="button" onClick={() => { setRenameEntry(null); setRenameName(''); }} className="px-3 py-2 text-sm">Cancel</button><button type="submit" className={primaryClass} disabled={!renameName.trim()}>Save</button></form> : <>
                <button type="button" onClick={() => { if (newName.trim() && !window.confirm('Discard the new plan name?')) return; selectPlan(entry.id); setShowPlans(false); setNewName(''); setShowCreate(false); }} className={`min-w-0 flex-1 text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 ${entry.id === plan?.id ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}><div className="font-medium truncate">{entry.name}{entry.id === plan?.id && <span className="ml-2 text-blue-600" aria-label="Current plan">✓</span>}</div><div className="text-xs text-gray-500 dark:text-gray-400">{entry.currency} · {entry.items?.length || 0} allocations{entry.updatedAt && ` · Updated ${new Date(entry.updatedAt).toLocaleString()}`}</div></button>
                <div className="flex items-center gap-1 px-2"><button type="button" onClick={() => { setRenameEntry(entry); setRenameName(entry.name); }} aria-label={`Rename ${entry.name}`} title="Rename" className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button><button type="button" onClick={() => removePlan(entry)} aria-label={`Delete ${entry.name}`} title="Delete" className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button></div>
              </>}
            </div>)}
          </div></div>
        </fieldset>
      </PlannerModal>}
      {completeRow && <PlannerModal title="Complete allocation" busy={busy} onClose={() => setCompleteRow(null)}>
        <p className="mb-4 text-sm">Did you buy {completeRow.stock.name}? Record the actual fill to update your position and complete this allocation together, or mark it done without a trade.</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => setCompleteRow(null)} className={buttonClass}>Cancel</button>
          <button type="button" onClick={() => { changeRow(identity(completeRow), { completed: true }); setCompleteRow(null); }} className={buttonClass}>Done without trade</button>
          <button type="button" onClick={() => { setBuyRow(completeRow); setCompleteRow(null); }} className={primaryClass}>Record filled buy</button>
        </div>
      </PlannerModal>}
      {showListing && <PlannerModal title={editRow ? 'Edit allocation' : 'Add allocation'} busy={busy} onClose={() => { if (((editRow ? search !== editRow.stock.name : selection || search.trim()) || amount !== (editRow ? String(editRow.amount) : defaultStockBudget(portfolioId)) || targetLocationId !== (editRow?.targetLocationId || '')) && !window.confirm('Discard this listing edit?')) return; resetEntry(); }}>
        <fieldset disabled={busy}>
          <div className="flex flex-col sm:flex-row items-start gap-3 mb-4">
            <label className="block min-w-0 flex-1 w-full text-sm font-medium">Stock / ETF<input id="stock-allocation-search" autoFocus disabled={Boolean(editRow)} autoComplete="off" className={`${inputClass} mt-1`} placeholder="Search name or symbol…" value={search} onChange={(event) => { setSelection(null); setSearch(event.target.value); }} /></label>
            <label className="block w-full sm:w-40 text-sm font-medium">Amount ({plan.currency})<input aria-label="Allocation budget" inputMode="decimal" className={`${inputClass} mt-1`} value={amount} onChange={(event) => setAmount(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addRow(); } }} /></label>
          </div>
          {positionsLoading && !selection && <p role="status" className="mb-3 text-sm">Loading existing positions…</p>}
          {positionsError && !selection && <p role="alert" className="mb-3 text-sm text-red-600">Existing positions unavailable: {positionsError}. You can still search listings.</p>}
          {searching && <p role="status" className="mb-3 text-sm">Searching listings…</p>}
          {searchError && <p role="alert" className="mb-3 text-sm text-red-600">{searchError}</p>}
          {!selection && suggestions.length > 0 && <ul aria-label="Stock listing suggestions" className="mb-4 max-h-64 overflow-y-auto rounded border divide-y dark:border-gray-700 dark:divide-gray-700">{suggestions.map((stock, index) => <li key={`${stock.stockId || stock.symbol}-${stock.exchange}-${index}`}><button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-gray-700 dark:focus:bg-gray-700" onClick={() => { setSelection(stock); setSearch(stock.name); setResults([]); }}><strong>{stock.name}</strong> ({stock.symbol})<span className="block text-xs text-gray-500">{stock.exchange || 'Venue unavailable'} · {stock.currency || 'Currency unavailable'} · {stock.instrumentType}{positionSuggestions.some((position) => position.stockId === stock.stockId) && ' · Existing position'}</span></button></li>)}</ul>}
          {!searching && !positionsLoading && !searchError && !selection && suggestions.length === 0 && <p className="mb-4 text-sm text-gray-500">{search.trim().length >= 2 ? 'No listings found.' : 'Type to search for a listing.'}</p>}
          {selection && <p className="mb-4 text-xs text-gray-500">{selection.symbol} · {selection.exchange} · {selection.currency} · {selection.instrumentType}</p>}
          {selection && <AllocationLocationSelector {...locationProps} selectedLocationId={targetLocationId} onSelect={(id) => { if (alive.current && listingSession === listingSessionVersion.current) setTargetLocationId(id || ''); }} />}
          <p className="mt-3 text-xs text-gray-500">Zero is allowed. Listing and estimates are verified when you save the plan.</p>
          {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 mt-6"><button type="button" onClick={resetEntry} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">Cancel</button><button type="button" disabled={!selection || !validAmount(amount)} className={primaryClass} onClick={addRow}>{editRow ? 'Apply' : 'Add'}</button></div>
        </fieldset>
      </PlannerModal>}
      {buyRow && <StockTransactionModal
        isOpen stockId={buyRow.stock.id} portfolioId={portfolioId}
        stockCurrency={buyRow.stock.currency} currentPrice={buyRow.nativePrice}
        defaultLocationId={buyRow.targetLocationId || ''} lockLocation hideNotes buyOnly requireConfirmation
        onClose={() => setBuyRow(null)} onSubmit={recordBuy}
        onSaved={async () => {
          // Never propagate read-back failure to the transaction form's submit/retry path.
          try {
            const next = await fetchPlan(plan.id);
            if (alive.current) { accept(next); setNotice('Buy recorded and allocation completed.'); }
          } catch (failure) { if (alive.current) setError(`Buy recorded. Plan refresh failed; use Refresh. ${errorText(failure)}`); }
        }}
        transactionContext={<div className="mb-4 rounded bg-blue-50 p-3 text-sm dark:bg-blue-950">
          <p className="font-semibold">{buyRow.stock.name} · {buyRow.stock.symbol} · {buyRow.stock.exchange}</p>
          <p>Plan budget: <StockAllocationValue value={buyRow.amount} currency={plan.currency} />. Estimated shares: <StockAllocationValue value={buyRow.estimatedQuantity} kind="amount" />.</p>
          <p>Saved custody target: {locations.find((location) => location.id === buyRow.targetLocationId)?.name || buyRow.targetLocation?.name || 'Unassigned'}.</p>
          <p>Native quote: <StockAllocationValue value={buyRow.nativePrice} currency={buyRow.stock.currency} kind="price" />. Enter the actual filled trade below; budget is not the share price.</p>
        </div>}
      />}
    </div>
  );
};

const StockAllocationPlannerPage = () => {
  const { currentPortfolio, currentPortfolioId } = usePortfolioContext();
  if (!currentPortfolioId) return <PortfolioRequired title="Stock allocation planner" description="Select a portfolio to manage its stock allocation plans." />;
  return <StockAllocationPlanner key={currentPortfolioId} portfolio={currentPortfolio} />;
};

export default StockAllocationPlannerPage;
